import { useEffect, useRef } from "react";

import { getSupabaseClient } from "../../providers/supabase/supabase";
import { PENDING_CALL_EVENT, readPendingCall } from "../mobile/pendingCall";

/** One heartbeat a minute while the CRM is open. */
const BEAT_MS = 60_000;
/** At the screen = a click, tap or key in the last 3 minutes. */
const INPUT_MS = 3 * 60_000;
/** Do not send more than one ping every 20 s, whatever happens. */
const MIN_GAP_MS = 20_000;

const ping = (active: boolean, device: "mobile" | "desktop", calling = false) =>
  getSupabaseClient()
    .rpc("gula_presence_ping", {
      p_active: active,
      p_device: device,
      p_calling: calling,
    })
    .then(
      () => undefined,
      () => undefined,
    );

/**
 * Tells the CRM that this person has it open and whether they are working, so
 * admins see on Inicio who is connected. Invisible; mounted in both layouts.
 */
export const PresenceHeartbeat = ({
  device,
}: {
  device: "mobile" | "desktop";
}) => {
  const lastInput = useRef(Date.now());
  const lastPing = useRef(0);
  const wasActive = useRef(false);

  useEffect(() => {
    const isActive = () =>
      document.visibilityState === "visible" &&
      Date.now() - lastInput.current < INPUT_MS;
    const beat = (force = false) => {
      const now = Date.now();
      if (!force && now - lastPing.current < MIN_GAP_MS) return;
      lastPing.current = now;
      wasActive.current = isActive();
      void ping(wasActive.current, device);
    };
    // First input after a pause: say so at once (does not wait a minute)
    const onInput = () => {
      lastInput.current = Date.now();
      if (!wasActive.current) beat();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        lastInput.current = Date.now();
        beat(true);
      }
    };
    // «Llamar por WhatsApp» leaves the CRM: they are calling, not away
    const onCall = () => {
      if (!readPendingCall()) return;
      lastPing.current = Date.now();
      wasActive.current = false;
      void ping(false, device, true);
    };

    beat(true);
    const timer = window.setInterval(() => beat(), BEAT_MS);
    window.addEventListener("pointerdown", onInput);
    window.addEventListener("keydown", onInput);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener(PENDING_CALL_EVENT, onCall);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("pointerdown", onInput);
      window.removeEventListener("keydown", onInput);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener(PENDING_CALL_EVENT, onCall);
    };
  }, [device]);

  return null;
};
